import type React from 'react';
import { useState, useEffect } from 'react';

import { useTz } from '@entities/schedules';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import { ArrowIosLeft, ArrowIosRight } from '@shared/assets';

import * as st from './styles.module.scss';

interface ArrowProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	direction: string;
}

const ButtonArrow: React.FC<ArrowProps> = ({ direction, ...props }) => {
	return (
		<button className={st.more} {...props}>
			<div className={st.icon}>{direction === 'left' ? <ArrowIosLeft /> : <ArrowIosRight />}</div>
		</button>
	);
};

interface SessionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	startTime: Date;
}

const ButtonSession: React.FC<SessionProps> = ({ startTime, ...props }) => {
	return (
		<button className={st.session} {...props}>
			{format(startTime, 'HH:mm')}
		</button>
	);
};

interface ScrollerProps {
	sessions: any[];
	onClick: (id: number) => void;
	quantity: number;
}

export const Scroller: React.FC<ScrollerProps> = ({ sessions, onClick, quantity }) => {
	const [start, setStart] = useState(0);
	const [end, setEnd] = useState(quantity);
	const [index, setIndex] = useState(0);
	const { tz } = useTz();

	useEffect(() => {
		setStart(0);
		setEnd(calculateQuantity(quantity));
		/* eslint-disable-next-line */
	}, [sessions]);

	function calculateQuantity(quantity: number) {
		return quantity <= sessions.length ? quantity - 1 : quantity;
	}

	const handleScroll = (direction: string) => {
		if (direction === 'forward') {
			setIndex(index + 1);
			setStart(end);

			if (end + quantity < sessions.length) {
				setEnd(end + quantity - 2);
			}

			if (end + quantity >= sessions.length) {
				setEnd(end + quantity - 1);
			}
		}

		if (direction === 'backward') {
			setIndex(index - 1);

			if (start - quantity > quantity) {
				setEnd(start);
				setStart(start - quantity + 2);
			}

			if (start - quantity <= quantity && start - quantity > 0) {
				setEnd(start);
				setStart(start - quantity + 2);
			}

			if (start - quantity <= 0) {
				setStart(0);
				setEnd(quantity - 1);
			}
		}
	};

	const showForwardBtn = () => {
		return end < sessions.length && quantity < sessions.length;
	};

	const showBackwardBtn = () => {
		return start > 0 && quantity < sessions.length;
	};

	return (
		<div className={st.scroller}>
			{showBackwardBtn() && (
				<ButtonArrow
					direction={'left'}
					onClick={(e) => {
						e.stopPropagation();
						handleScroll('backward');
					}}
				/>
			)}
			{sessions.slice(start, end).map((session) => (
				<ButtonSession
					key={session.id}
					startTime={utcToZonedTime(parseISO(session.start_time), tz)}
					onClick={(e) => {
						e.stopPropagation();
						onClick(session.id);
					}}
				/>
			))}
			{showForwardBtn() && (
				<ButtonArrow
					direction={'right'}
					onClick={(e) => {
						e.stopPropagation();
						handleScroll('forward');
					}}
				/>
			)}
		</div>
	);
};
